import { Divider, Group, Modal, MultiSelect, Paper, SimpleGrid, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { useMemo } from 'react';
import { FIELD_GROUP_COLUMN_NAMES, FIELD_GROUPS } from '../../ColumnMapping/section/columnSection';
import useGetColumnMapping from '../../ColumnMapping/hooks/useGetColumnMapping';
import { aiAgentApis } from '../../../../../../../shared/apis/ai-agent/apis';

const normalizeMappingValueToColumnName = (mappingValue: string) => {
  if (mappingValue === 'PROCESS_CHARGE_TYPE') {
    return 'charge_type';
  }

  return mappingValue.toLowerCase();
};

export default function TemplateDetailModal(props: {
  opened: boolean;
  onClose: () => void;
  templateId: number;
  headerIndex: number;
}) {
  const { opened, onClose, templateId, headerIndex } = props;
  const { data: templateDetail } = useQuery(
    ['getAsCsTemplate', templateId],
    () => aiAgentApis.template.getAsCsTemplate(templateId),
    {
      enabled: !!templateId,
    },
  );
  const { columnInfoMap } = useGetColumnMapping();
  const templateColumnInfoMap = useMemo(() => {
    return Object.entries(columnInfoMap).reduce<Record<string, string>>((acc, [columnName, columnInfo]) => {
      acc[columnName] = columnInfo.name;
      return acc;
    }, {});
  }, [columnInfoMap]);

  // mapping 데이터를 역직렬화하여 각 필드별로 fieldId 배열로 변환
  const deserializedMapping = useMemo(() => {
    const fields = templateDetail?.data?.data?.field || [];

    // mapping 데이터를 columnName 키별로 그룹핑 (mappingOrder 순서 유지)
    const mappingByKey: Record<string, Array<{ order: number; fieldId: string }>> = {};

    // 각 field를 순회하면서 mapping 데이터 수집
    fields.forEach(
      (field: {
        id: number;
        name: string;
        mapping?: Array<{
          id: number;
          columnName: string;
          mappingOrder: number;
        }>;
      }) => {
        if (field.mapping && field.mapping.length > 0) {
          field.mapping.forEach((mappingItem) => {
            const mappingKey = normalizeMappingValueToColumnName(mappingItem.columnName);

            if (!mappingByKey[mappingKey]) {
              mappingByKey[mappingKey] = [];
            }
            mappingByKey[mappingKey].push({
              order: mappingItem.mappingOrder,
              fieldId: field.id.toString(),
            });
          });
        }
      },
    );

    // 각 필드별로 mappingOrder 순서대로 정렬 후 fieldId만 추출
    const result: Record<string, string[]> = {};
    FIELD_GROUP_COLUMN_NAMES.forEach((key) => {
      if (mappingByKey[key]) {
        result[key] = mappingByKey[key].sort((a, b) => a.order - b.order).map((item) => item.fieldId);
      } else {
        result[key] = [];
      }
    });

    return { mapping: result, fields };
  }, [templateDetail]);

  return (
    <Modal centered opened={opened} onClose={onClose} title={`템플릿 상세`} size="100%">
      <Paper withBorder p="md" my="md">
        {headerIndex && (
          <Text mb="md" fw={600}>
            참조 문서 Header 열 번호: {headerIndex}
          </Text>
        )}
        {FIELD_GROUPS.map((group, groupIndex) => (
          <div key={groupIndex}>
            {groupIndex > 0 && <Divider my="lg" />}
            <SimpleGrid cols={4} {...(groupIndex === 1 ? { my: 'lg' } : {})}>
              {group.map((fieldKey) => (
                <Group key={fieldKey} gap="sm" align="center">
                  <Text size="sm">{templateColumnInfoMap[fieldKey] || fieldKey}</Text>
                  <MultiSelect
                    value={deserializedMapping.mapping[fieldKey] || []}
                    disabled
                    data={
                      deserializedMapping.fields?.map((field: { id: number; name: string }) => ({
                        value: field.id.toString(),
                        label: field.name,
                      })) || []
                    }
                    style={{ flex: 1 }}
                    readOnly
                  />
                </Group>
              ))}
            </SimpleGrid>
          </div>
        ))}
      </Paper>
    </Modal>
  );
}
